import { useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useSelector } from 'react-redux';

import { RootState } from '@/store';
import { useAppDispatch } from '@/store/hooks';
import {
  selectPatientDetails,
  selectPatientSettings
} from '@/store/selectors/transmissionReportPatientSelectors';
import {
  useCancelAppointmentsMutation,
  useRunSchedulerMutation
} from '@/store/slices/api/appointmentApiSlice';
import { useGetOrgByIdQuery } from '@/store/slices/api/organizationApiSlice';
import { patientsApiSlice, useGetPatientQuery } from '@/store/slices/api/patientsApiSlice';
import { useUpdatePatientMutation } from '@/store/slices/api/transmissionReport/reportsPatientApiSlice';
import { API_SLICE } from '@/utils/constants/apiEndpoints';
import { isInactiveDeviceStatus } from '@/utils/deviceStatus';
import { isIlrDevice } from '@/utils/deviceType';
import {
  clinicSchedulesAutomatically,
  remoteMonitoringTurnedOff,
  remoteMonitoringTurnedOn,
  runSchedulerForDevices,
  schedulableDeviceIds,
  scheduleSettingsChanged
} from '@/utils/runPatientScheduler';

import { LabelAndValue } from '@/types/custom';
import Dropdown from '@/ui/Dropdown';
import LabeledField from '@/ui/LabeledField';
import SkeletonGrid from '@/ui/Skeleton';

import styles from './SettingsSection.module.scss';

interface PatientSettings {
  isHfMonitored?: boolean;
  shouldOnlyMonitorHf?: boolean;
  shouldOnlyMonitorAlerts?: boolean;
  cycleIcdPm?: number;
  cycleHf?: number;
  cycleIlr?: number;
  inClinicBuffer?: number;
}

type SettingsField = keyof PatientSettings | 'isRemoteMonitored' | 'isRhythmMonitored';

interface FieldConfig {
  label: string;
  value: string;
  field: SettingsField;
  options: LabelAndValue[];
  currentValue: string;
  disabled?: boolean;
}

interface SettingsSectionProps {
  patientId: string;
  reportId?: number;
}

const YES_NO_OPTIONS: LabelAndValue[] = [
  { value: '', label: '-' },
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' }
];

const HF_ILR_CYCLE_OPTIONS: LabelAndValue[] = [
  { value: '31', label: '31 Days' },
  { value: '35', label: '35 Days' },
  { value: '60', label: '60 Days' },
  { value: '91', label: '91 Days' },
  { value: '95', label: '95 Days' },
  { value: '180', label: '180 Days' }
];

const ICD_PM_CYCLE_OPTIONS: LabelAndValue[] = [
  { value: '91', label: '91 Days' },
  { value: '95', label: '95 Days' },
  { value: '180', label: '180 Days' },
  { value: '365', label: '365 Days' }
];

const BUFFER_OPTIONS: LabelAndValue[] = [
  { value: '14', label: '14 Days' },
  { value: '30', label: '30 Days' },
  { value: '60', label: '60 Days' }
];

const formatBoolean = (value?: boolean | null): string =>
  value == null ? '-' : value ? 'Yes' : 'No';
const formatCycle = (value?: number): string => (value ? `${value} Days` : '-');

const processFieldValue = (value: string): boolean | number | string | null => {
  if (value === '') return null;
  if (value === 'true') return true;
  if (value === 'false') return false;
  const numValue = parseInt(value, 10);
  return isNaN(numValue) ? value : numValue;
};

const SettingsSection = ({ patientId, reportId }: SettingsSectionProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localSettings, setLocalSettings] = useState<PatientSettings | null>(null);
  const [remoteMonitored, setRemoteMonitored] = useState<boolean | null>(null);
  const [rhythmMonitored, setRhythmMonitored] = useState<boolean | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const patientSettings = useSelector((state: RootState) =>
    selectPatientSettings(state, patientId)
  );
  const patientDetails = useSelector((state: RootState) => selectPatientDetails(state, patientId));
  const { data: fullPatient, isLoading: patientLoading } = useGetPatientQuery(patientId, {
    skip: !patientId
  });
  const clinicId = fullPatient?.clinic?.id ?? patientDetails?.clinic?.id ?? '';
  const {
    data: clinicData,
    isLoading: clinicLoading,
    isError: clinicError
  } = useGetOrgByIdQuery(clinicId, { skip: !clinicId });
  const clinicPending = patientLoading || (Boolean(clinicId) && clinicLoading && !clinicError);
  const canSetSchedules = clinicSchedulesAutomatically(clinicData?.organization);

  const activeDevices = (fullPatient?.matchDevices ?? patientDetails?.matchDevices ?? []).filter(
    (device) => !isInactiveDeviceStatus(device.status)
  );
  const hasActiveIlr = activeDevices.some((device) => isIlrDevice(device.type));
  const onlyActiveIlr =
    activeDevices.length > 0 && activeDevices.every((device) => isIlrDevice(device.type));

  const [updatePatient] = useUpdatePatientMutation();
  const [cancelAppointments] = useCancelAppointmentsMutation();
  const [runScheduler] = useRunSchedulerMutation();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (patientSettings && !localSettings) {
      setLocalSettings({ ...patientSettings });
    }
  }, [patientSettings, localSettings]);

  useEffect(() => {
    if (!isEditing && fullPatient) {
      setRemoteMonitored(
        typeof fullPatient.isRemoteMonitored === 'boolean' ? fullPatient.isRemoteMonitored : null
      );
      setRhythmMonitored(
        typeof fullPatient.isRhythmMonitored === 'boolean' ? fullPatient.isRhythmMonitored : null
      );
    }
  }, [fullPatient, isEditing]);

  const rhythmEditable = remoteMonitored !== false;

  const hfMonitoringOn = localSettings?.isHfMonitored === true;

  const fieldConfigs = useMemo((): FieldConfig[] => {
    if (!localSettings) return [];

    return [
      {
        label: 'Remote Monitored',
        value: formatBoolean(remoteMonitored),
        field: 'isRemoteMonitored',
        options: YES_NO_OPTIONS,
        currentValue: remoteMonitored == null ? '' : String(remoteMonitored)
      },
      {
        label: 'Monitored In Rhythm',
        value: !rhythmEditable ? '-' : formatBoolean(rhythmMonitored),
        field: 'isRhythmMonitored',
        disabled: !rhythmEditable,
        options: YES_NO_OPTIONS.filter((option) => option.value !== ''),
        currentValue: !rhythmEditable || rhythmMonitored == null ? '' : String(rhythmMonitored)
      },
      {
        label: 'HF Monitoring',
        value: formatBoolean(localSettings.isHfMonitored),
        field: 'isHfMonitored',
        disabled: false,
        options: YES_NO_OPTIONS,
        currentValue: localSettings.isHfMonitored == null ? '' : String(localSettings.isHfMonitored)
      },
      ...(hfMonitoringOn
        ? [
            {
              label: 'HF Only Monitoring',
              value: formatBoolean(localSettings.shouldOnlyMonitorHf),
              field: 'shouldOnlyMonitorHf' as const,
              disabled: false,
              options: YES_NO_OPTIONS,
              currentValue:
                localSettings.shouldOnlyMonitorHf == null
                  ? ''
                  : String(localSettings.shouldOnlyMonitorHf)
            }
          ]
        : []),
      {
        label: 'Alert Only Monitoring',
        value: formatBoolean(localSettings.shouldOnlyMonitorAlerts),
        field: 'shouldOnlyMonitorAlerts',
        options: YES_NO_OPTIONS,
        currentValue:
          localSettings.shouldOnlyMonitorAlerts == null
            ? ''
            : String(localSettings.shouldOnlyMonitorAlerts)
      },
      ...(!onlyActiveIlr
        ? [
            {
              label: 'Cycle ICD/PM',
              value: formatCycle(localSettings.cycleIcdPm),
              field: 'cycleIcdPm' as const,
              disabled: false,
              options: ICD_PM_CYCLE_OPTIONS,
              currentValue: String(localSettings.cycleIcdPm ?? '')
            }
          ]
        : []),
      ...(hfMonitoringOn
        ? [
            {
              label: 'Cycle HF',
              value: formatCycle(localSettings.cycleHf),
              field: 'cycleHf' as const,
              disabled: false,
              options: HF_ILR_CYCLE_OPTIONS,
              currentValue: String(localSettings.cycleHf ?? '')
            }
          ]
        : []),
      ...(hasActiveIlr
        ? [
            {
              label: 'Cycle ILR',
              value: (localSettings.cycleIlr ?? 0) > 0 ? formatCycle(localSettings.cycleIlr) : '-',
              field: 'cycleIlr' as const,
              disabled: false,
              options: HF_ILR_CYCLE_OPTIONS,
              currentValue: String(localSettings.cycleIlr ?? '')
            }
          ]
        : []),
      {
        label: 'In Clinic Buffer',
        value: formatCycle(localSettings.inClinicBuffer),
        field: 'inClinicBuffer',
        options: BUFFER_OPTIONS,
        currentValue: String(localSettings.inClinicBuffer ?? '')
      }
    ];
  }, [
    hasActiveIlr,
    hfMonitoringOn,
    localSettings,
    onlyActiveIlr,
    remoteMonitored,
    rhythmEditable,
    rhythmMonitored
  ]);

  const handleFieldChange = useCallback((field: SettingsField, value: string) => {
    const processedValue = processFieldValue(value);
    if (field === 'isRemoteMonitored') {
      setRemoteMonitored(typeof processedValue === 'boolean' ? processedValue : null);
      return;
    }
    if (field === 'isRhythmMonitored') {
      setRhythmMonitored(typeof processedValue === 'boolean' ? processedValue : null);
      return;
    }
    setLocalSettings((prev) => (prev ? { ...prev, [field]: processedValue } : null));
  }, []);

  const handleSave = useCallback(async () => {
    if (!localSettings || clinicPending) return;

    setIsUpdating(true);
    try {
      const updatePayload = {
        patient: {
          id: patientId,
          settings: localSettings,
          ...(typeof remoteMonitored === 'boolean' ? { isRemoteMonitored: remoteMonitored } : {}),
          ...(rhythmEditable && typeof rhythmMonitored === 'boolean'
            ? { isRhythmMonitored: rhythmMonitored }
            : {})
        }
      };
      await updatePatient({ ...updatePayload, activeReportId: reportId }).unwrap();
      const turnedOffRemoteMonitoring = remoteMonitoringTurnedOff(
        fullPatient?.isRemoteMonitored,
        remoteMonitored
      );
      const refreshSchedules =
        scheduleSettingsChanged(patientSettings, localSettings) ||
        remoteMonitoringTurnedOn(fullPatient?.isRemoteMonitored, remoteMonitored);
      let cancelFailed = false;
      if (turnedOffRemoteMonitoring) {
        try {
          await cancelAppointments({ patientId }).unwrap();
        } catch (error) {
          cancelFailed = true;
          console.error('Remote schedules were not cancelled', error);
        }
      }
      const clinicBlockedScheduler = clinicError && refreshSchedules && !turnedOffRemoteMonitoring;
      const failed =
        turnedOffRemoteMonitoring || !canSetSchedules || !refreshSchedules || clinicBlockedScheduler
          ? 0
          : await runSchedulerForDevices(
              runScheduler,
              schedulableDeviceIds(fullPatient?.matchDevices ?? patientDetails?.matchDevices)
            );
      dispatch(
        patientsApiSlice.util.invalidateTags([
          { type: API_SLICE.Patient, id: `${patientId}-schedules` }
        ])
      );

      notifications.show({
        title: 'Success',
        message: 'Patient settings updated successfully',
        color: 'green'
      });
      if (cancelFailed) {
        notifications.show({
          title: 'Schedules not cancelled',
          message: 'The settings were saved. Active remote schedules could not be cancelled.',
          color: 'yellow'
        });
      }
      if (clinicBlockedScheduler) {
        notifications.show({
          title: 'Schedule not adjusted',
          message:
            'The settings were saved. Clinic settings could not be loaded, so device schedules were not refreshed.',
          color: 'yellow'
        });
      } else if (failed > 0) {
        notifications.show({
          title: 'Schedule not adjusted',
          message: 'The settings were saved. One or more device schedules could not be updated.',
          color: 'yellow'
        });
      }

      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update settings:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update patient settings',
        color: 'red'
      });
    } finally {
      setIsUpdating(false);
    }
  }, [
    cancelAppointments,
    canSetSchedules,
    clinicError,
    clinicPending,
    dispatch,
    fullPatient?.isRemoteMonitored,
    fullPatient?.matchDevices,
    localSettings,
    patientDetails?.matchDevices,
    patientId,
    patientSettings,
    reportId,
    remoteMonitored,
    rhythmEditable,
    rhythmMonitored,
    runScheduler,
    updatePatient
  ]);

  const createCustomEditableComponent = useCallback(
    (field: FieldConfig) => (
      <Dropdown
        options={field.options}
        value={field.options.find((opt) => opt.value === field.currentValue)}
        isDisabled={field.disabled || isUpdating}
        onChange={(val) => {
          if (val && !Array.isArray(val)) {
            handleFieldChange(field.field, val.value);
          }
        }}
      />
    ),
    [handleFieldChange, isUpdating]
  );

  const renderFields = useCallback(
    () => (
      <div className={styles.fields}>
        {fieldConfigs.map((field) => (
          <div key={field.field} className={styles.field}>
            <LabeledField
              label={field.label}
              labelVariant="sentence"
              editOnHover
              value={field.value}
              showEditIcon={!isEditing}
              onEdit={() => setIsEditing(true)}
              isEditable={isEditing && !isUpdating}
              customEditableComponent={
                isEditing && !isUpdating ? createCustomEditableComponent(field) : undefined
              }
            />
          </div>
        ))}
      </div>
    ),
    [createCustomEditableComponent, fieldConfigs, isEditing, isUpdating]
  );

  if (!patientId || !localSettings) {
    return (
      <>
        <SkeletonGrid columns={1} rows={1} height={20} />
        <SkeletonGrid columns={3} rows={1} height={30} />
        <SkeletonGrid columns={4} rows={1} height={30} />
      </>
    );
  }

  if (!patientSettings) return <div>No patient settings available</div>;

  return (
    <div>
      {isEditing && (
        <div className={styles.buttonContainer}>
          <Button
            className={styles.saveBtn}
            size="compact-sm"
            onClick={handleSave}
            disabled={isUpdating || clinicPending}
            loading={isUpdating}>
            {isUpdating ? 'Saving...' : 'Save'}
          </Button>
        </div>
      )}
      {renderFields()}
    </div>
  );
};

export default SettingsSection;
